/**
 * Roleplay card component styles
 */
export const roleplayCardStyles = {
  // Base styles
  base: [
  'bg-neutral-white',
  'border',
  'border-neutral-gray-400',
  'border-solid',
  'flex',
  'flex-col',
  'items-start',
  'rounded-lg',
  'w-full',
].join(' '),

  // Header section
  header: [
    'box-border',
    'flex',
    'items-center',
    'justify-between',
    'overflow-hidden',
    'px-6',
    'py-4',
    'relative',
    'rounded-lg',
    'w-full',
    'gap-4',
    'md:gap-6',
    'xl:gap-10',
  ].join(' '),

  headerLeft: [
    'flex',
    'gap-2',
    'lg:gap-4',
    'items-center',
    'relative',
    'w-full',
    'truncate',
    // 'max-w-full',
    'flex-1'
  ].join(' '),

  playIcon: [
    'overflow-hidden',
    'relative',
    'shrink-0',
    'w-6',
    'h-6',
  ].join(' '),

  title: [
    'md:text-base',
    'xl:text-lg',
    '2xl:text-xl',
    'text-neutral-black',
    'truncate',
    'font-bold'
  ].join(' '),

  bestScoreContainer: [
    'flex',
    'items-center',
    'justify-end',
    'relative',
  ].join(' '),

  headerRight: [
    'flex',
    'gap-6',
    'items-center',
    'justify-end',
    'relative',
  ].join(' '),

  viewDetailsButton: [
    'flex',
    'gap-2',
    'items-center',
    'relative',
    'cursor-pointer',
    'bg-transparent',
    'border-none',
  ].join(' '),

  viewDetailsText: [
    'typography-button-small',
    'text-inherit',
    'text-center',
    'whitespace-nowrap',
    'pointer-events-none',
    'hidden',
    'md:inline-block',
    'lg:hidden',
    'xl:inline-block'
  ].join(' '),

  // Body section
  body: [
    'box-border',
    'flex',
    'flex-col',
    'gap-4',
    'items-start',
    'overflow-hidden',
    'pb-6',
    'pt-0',
    'px-6',
    'relative',
    'rounded-lg',
    'w-full',
  ].join(' '),

  scoreTag: [
    'bg-neutral-gray-200',
    'box-border',
    'flex',
    'gap-3',
    'items-center',
    'px-3',
    'py-0.5',
    'relative',
    'rounded',
    'whitespace-nowrap',
  ].join(' '),

  scoreText: [
    'flex',
    'flex-col',
    'typography-button-small',
    'justify-center',
    'relative',
    'text-neutral-gray-800',
  ].join(' '),
} as const;

/**
 * Roleplay card intro component styles
 */
export const roleplayCardIntroStyles = {
  // Base styles
  base: [
  'box-border',
  'flex',
  'flex-col',
  'gap-10',
  'items-start',
  'relative',
  'rounded-lg',
  'w-full',
].join(' '),

  // Content section
  content: [
    'content-stretch',
    'flex',
    'flex-col',
    'sm:flex-row',
    'items-center',
    'sm:items-start',
    'justify-between',
    'relative',
    'w-full',
    'gap-4',
    'sm:gap-2',
  ].join(' '),

  // Description section
  descriptionContainer: [
    'sm:basis-0',
    'flex',
    'flex-col',
    'grow',
    'gap-4',
    'justify-center',
    'min-h-px',
    'min-w-px',
    'relative',
    'self-stretch',
  ].join(' '),

  description: [
    'font-bold',
    'leading-6',
    'text-neutral-black',
    'text-xs',
    'lg:text-sm',
    'md:text-base',
    'xl:text-base',
  ].join(' '),

  // Avatar section
  avatarContainer: [
    'content-stretch',
    'flex',
    'flex-col',
    'gap-3',
    'items-center',
    'justify-center',
    'relative',
    'w-[140px]',
    'mb-auto',
  ].join(' '),

  avatar: [
    'border',
    'border-neutral-gray-200',
    'border-solid',
    'relative',
    'rounded-full',
    'w-16',
    'h-16',
    'overflow-hidden',
  ].join(' '),

  avatarImage: [
    'absolute',
    'inset-0',
    'h-full',
    'w-full',
    'max-w-none',
    'object-cover',
    'object-center',
    'rounded-full',
  ].join(' '),

  avatarName: [
    'typography-body-small',
    'text-neutral-gray-800',
    'text-center',
    'leading-[19px]',
    'min-w-full',
    'w-min',
  ].join(' '),
} as const;

/**
 * Roleplay card history component styles
 */
export const roleplayCardHistoryStyles = {
  // Base styles
  base: [
    'flex',
    'flex-col',
    'gap-4',
    'items-start',
    'rounded-lg',
    'w-full',
  ].join(' '),

  // Title
  title: [
    'typography-body-title',
    'text-neutral-black',
  ].join(' '),

  // List container with scrolling
  listContainer: [
    'box-border',
    'flex',
    'flex-col',
    'items-start',
    'overflow-y-auto',
    'overflow-x-hidden',
    'relative',
    'rounded-lg',
    'w-full',
  ].join(' '),

  // List item
  listItem: [
    'border-b',
    'border-neutral-gray-400',
    'border-solid',
    'first:pt-0',
    'last:border-b-0',
    'last:pb-0',
    'py-2',
    'w-full',
  ].join(' '),
} as const;

/**
 * Roleplay card history item component styles
 */
export const roleplayCardHistoryItemStyles = {
  // Base styles
  base: [
    'box-border',
    'flex',
    'items-center',
    'justify-between',
    'relative',
    'w-full',
    'overflow-x-auto',
    'overflow-y-hidden',
    'gap-4',

    // Allows space for scrollbars, especially on Windows where scrollbars take 
    // up space and can cause layout shifts. By adding padding, we ensure that 
    // content doesn't get cut off or hidden behind scrollbars.
    'pt-0 px-0 pb-2 sm:pb-0 lg:pb-1.5 xl:pb-0',
  ].join(' '),

  // Remove border for last item
  lastItem: ['border-b-0'].join(' '),

  // Left side container
  sessionInfo: ['flex', 'gap-4', 'items-center', 'justify-center', 'relative'].join(' '),

  // Date and time container
  timeCompleted: [
    'flex',
    'typography-body',
    'gap-2',
    'items-center',
    'leading-0',
    'not-italic',
    'relative',
    'text-base',
  ].join(' '),

  // Date styling
  sessionDate: [
    'flex',
    'flex-col',
    'justify-center',
    'relative',
    'text-neutral-black',
    'w-[100px]',
    'leading-5',
  ].join(' '),

  // Time styling
  sessionTime: [
    'flex',
    'flex-col',
    'justify-center',
    'relative',
    'text-neutral-gray-700',
    'w-[65px]',
    'leading-5',
    'tracking-tight',
  ].join(' '),

  // Score tag container
  scoreTag: [
    'bg-neutral-gray-200',
    'box-border',
    'flex',
    'gap-3',
    'items-center',
    'max-w-[96px]',
    'px-3',
    'py-0.5',
    'relative',
    'rounded',
  ].join(' '),

  // Score text
  scoreText: [
    'flex',
    'flex-col',
    'typography-button-small',
    'font-medium',
    'justify-center',
    'leading-0',
    'not-italic',
    'relative',
    'text-neutral-gray-800',
    'text-sm',
    'w-[74px]',
    'leading-7',
    'whitespace-nowrap',
  ].join(' '),

  // Score delta container
  scoreDeltaContainer: ['flex', 'items-center', 'justify-center'].join(' '),

  // Score delta tag
  scoreDelta: [
    'bg-white',
    'box-border',
    'flex',
    'gap-1',
    'items-center',
    'justify-center',
    'px-1',
    'py-0.5',
    'relative',
    'rounded-full',
    'w-full',
    'text-system-success',
  ].join(' '),

  // Delta icon container
  deltaIcon: ['relative', 'size-4', 'text-inherit'].join(' '),

  // Delta text
  deltaText: [
    'flex',
    'flex-col',
    'typography-body-small',
    'font-bold',
    'justify-center',
    'leading-0',
    'not-italic',
    'relative',
    'text-inherit',
    'text-sm',
    'text-nowrap',
    'whitespace-none',
  ].join(' '),

  // Right side container
  sessionActions: ['flex', 'gap-4', 'items-center', 'justify-end', 'relative'].join(' '),

  // Action buttons
  sessionButton: [
    'box-border',
    'flex',
    'gap-2',
    'h-8',
    'items-center',
    'justify-center',
    'overflow-hidden',
    'px-2',
    'py-3',
    'relative',
    'rounded-full',
    'cursor-pointer',
    'bg-transparent',
    'border-none',
    'typography-body-small',
    'text-neutral-gray-800',
    'text-center',
    'whitespace-nowrap',
    'underline',
    'decoration-solid',
    'underline-offset-[25%]',
    'leading-[19px]',
    'text-sm',
    'hover:text-brand-primary',
    'transition-colors',
    'duration-200',
    'whitespace-pre',
    'disabled:pointer-events-none',
    'disabled:hover:text-neutral-gray-700',
    'disabled:no-underline',
  ].join(' '),

  sharedWithManagerLabel: [
    'h-8',
    'flex',
    'flex-row',
    'flex-nowrap',
    'gap-2',
    'items-center',
    'justify-center',
    'px-3',
    'py-2',
    'rounded-full',
    'bg-green-100',
    'text-green-800',
    'typography-body-small',
    'leading-5',
    'pointer-events-none',
    'whitespace-nowrap',
  ].join(' '),
} as const;
